import React, { createContext, useState, useContext, ReactNode } from "react";

interface SignupContextType {
  user: any; 
  setUser: (user: any) => void;
  cognitoUser: any;
  setCognitoUser: (user: any) => void;
  password: string;
  setPassword: (password: string) => void;
  phone_number: string;
  setPhoneNumber: (phone_number: string) => void;
  country_code: string;
  setCountryCode: (country_code: string) => void;
  fullNumber: string;
  setFullNumber: (fullNumber: string) => void;
  UUID: string | null;
  setUUID: (UUID: string | null) => void;
  academyId: string | null;
  setAcademyId: (academyId: string | null) => void;
  url: string | null;
  setUrl: (url: string | null) => void;
  username: string;
  setUserName: (value: string) => void;
  groupIds: string;
  setGroupIds: (value: string) => void;
  given_name: string;
  setGivenName: (value: string) => void;
  family_name: string;
  setFamilyName: (value: string) => void;
  companyName: string;
  setCompanyName: (value: string) => void;
  isNotPartOfCompany: boolean;
  setIsNotPartOfCompany: (value: boolean) => void;
  error: boolean;
  setError: (value: boolean) => void;
  cognitoError: Error | null;
  setCognitoError: (value: Error | null) => void;
  recaptchaToken: string | null;
  setRecaptchaToken: (value: string | null) => void;
  checked: boolean;
  setChecked: (value: boolean) => void;
  disclaimerError: boolean;
  setDisclaimerError: (value: boolean) => void;
  emailError: boolean;
  setEmailError: (value: boolean) => void;
  companyError: boolean;
  setCompanyError: (value: boolean) => void;
  invalidPassword: boolean;
  setInvalidPassword: (value: boolean) => void;
  userExists: boolean;
  setUserExists: (value: boolean) => void;
  incomplete: boolean;
  setIncomplete: (value: boolean) => void;
  phoneExists: boolean;
  setPhoneExists: (value: boolean) => void;
  emailCandidate: boolean;
  setEmailCandidate: (value: boolean) => void;
  allowedCountries: string[];
  setAllowedCountries: (value: string[]) => void;
  isDisabled: boolean;
  setIsDisabled: (value: boolean) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
}

export const SignupContext = createContext<SignupContextType | null>(null);

export const SignupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [cognitoUser, setCognitoUser] = useState<any>(null);
  const [password, setPassword] = useState<string>("");
  const [phone_number, setPhoneNumber] = useState<string>("");
  const [country_code, setCountryCode] = useState<string>("+1");
  const [fullNumber, setFullNumber] = useState<string>("");
  const [UUID, setUUID] = useState<string | null>(null);
  const [academyId, setAcademyId] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [username, setUserName] = useState<string>("");
  const [groupIds, setGroupIds] = useState<string>("");
  const [given_name, setGivenName] = useState<string>("");
  const [family_name, setFamilyName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [isNotPartOfCompany, setIsNotPartOfCompany] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [cognitoError, setCognitoError] = useState<Error | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<any>(null);
  const [checked, setChecked] = useState<boolean>(false);
  const [disclaimerError, setDisclaimerError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [companyError, setCompanyError] = useState<boolean>(false);
  const [invalidPassword, setInvalidPassword] = useState<boolean>(false);
  const [userExists, setUserExists] = useState<boolean>(false);
  const [incomplete, setIncomplete] = useState<boolean>(true);
  const [phoneExists, setPhoneExists] = useState<boolean>(false);
  const [emailCandidate, setEmailCandidate] = useState<boolean>(false);
  const [allowedCountries, setAllowedCountries] = useState<string[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const contextValue: SignupContextType = {
    user,
    setUser,
    cognitoUser,
    setCognitoUser,
    password,
    setPassword,
    phone_number,
    setPhoneNumber,
    country_code,
    setCountryCode,
    fullNumber,
    setFullNumber,
    UUID,
    setUUID,
    academyId,
    setAcademyId,
    url,
    setUrl,
    username,
    setUserName,
    groupIds,
    setGroupIds,
    given_name,
    setGivenName,
    family_name,
    setFamilyName,
    companyName,
    setCompanyName,
    isNotPartOfCompany,
    setIsNotPartOfCompany,
    error,
    setError,
    cognitoError,
    setCognitoError,
    recaptchaToken,
    setRecaptchaToken,
    checked,
    setChecked,
    disclaimerError,
    setDisclaimerError,
    emailError,
    setEmailError,
    companyError,
    setCompanyError,
    invalidPassword,
    setInvalidPassword,
    userExists,
    setUserExists,
    incomplete,
    setIncomplete,
    phoneExists,
    setPhoneExists,
    emailCandidate,
    setEmailCandidate,
    allowedCountries,
    setAllowedCountries,
    isDisabled,
    setIsDisabled,
    loading,
    setLoading,
  };

  return <SignupContext.Provider value={contextValue}>{children}</SignupContext.Provider>;
};

export const useSignup = () => {
  const context = useContext(SignupContext);
  if (!context) {
    throw new Error("useSignup must be used within a SignupProvider");
  }
  return context;
};
